import {
	Scene,
	Engine,
	Observable,
	PickingInfo,
	AbstractMesh,
	Camera,
	Color4,
	Matrix,
	Color3,
} from '@babylonjs/core';
import '@babylonjs/gui';

import ObservablesManager from './engine/ObservablesManager';
import CameraManager from './engine/CameraManager';
import MeshFactory from './engine/MeshFactory';
import LightFactory from './engine/LightFactory';
import PointerManager from './engine/PointerManager';
import CollisionManager from './engine/CollisionManager';

export class Engine3d {
	public cameraManager: CameraManager;
	public lightFactory: LightFactory;
	public meshFactory: MeshFactory;
	public pointerManager: PointerManager;
	public colissionManager: CollisionManager;

	protected scene: Scene;
	protected engine: Engine;
	protected canvas: HTMLCanvasElement;
	protected observablesManager: ObservablesManager;

	constructor(canvas: HTMLCanvasElement) {
		this.canvas = canvas;
		this.observablesManager = new ObservablesManager();
	}

	/**
	 * Initializes Babylon.
	 *
	 */
	public Initiate(): void {
		this.initEngine()
			.then((engine) => {
				this.onEngineInit.notifyObserversWithPromise(engine);
				return this.initScene(engine);
			})
			.then((scene) => {
				this.onSceneInit.notifyObserversWithPromise(scene);
			});
	}

	public destroy(): void {
		this.engine.dispose();
		this.scene.dispose();
	}

	/**
	 * Will notify when the scene was initialized.
	 */
	public get onSceneInit(): Observable<Scene> {
		return this.observablesManager.onSceneInitObservable;
	}

	/**
	 * Will notify when the engine was initialized.
	 */
	public get onEngineInit(): Observable<Engine> {
		return this.observablesManager.onEngineInitObservable;
	}

	/**
	 * Will notify on scene pick.
	 */
	public get onScenePick(): Observable<PickingInfo> {
		return this.pointerManager.onPointerPick;
	}

	/**
	 * Will notify when the collision report happend.
	 */
	public get onCollisionReport(): Observable<[AbstractMesh, AbstractMesh[]]> {
		return this.observablesManager.onCollisionObservable;
	}

	/**
	 * Will notify when the collision report happend.
	 */
	public get onCollisionEndReport(): Observable<AbstractMesh> {
		return this.observablesManager.onCollisionEndObservable;
	}

	public get onCameraViewUpdated(): Observable<Camera> {
		return this.observablesManager.onCameraViewUpdated;
	}

	/**
	 * Will notify when the scene resize happend.
	 */
	public get onSceneResized(): Observable<Engine> {
		return this.engine.onResizeObservable;
	}

	/**
	 * Renders scene.
	 */
	public renderScene = (): void => {
		this.scene.render();
	};

	/**
	 * Starts default render loop scene.
	 */
	public startRenderLoop(): void {
		this.engine.runRenderLoop(this.renderScene);
	}

	/**
	 * Stops default render loop scene.
	 */
	public stopRenderLoop(): void {
		this.engine.stopRenderLoop(this.renderScene);
	}

	/**
	 * Register callback function before scene render on each frame.
	 */
	public registerBeforeRender(callback: () => void): void {
		this.scene.onBeforeRenderObservable.add(callback);
	}

	/**
	 * Removes register callback function before scene render on each frame.
	 */
	public unregisterBeforeRender(callback: () => void): void {
		this.scene.onBeforeRenderObservable.removeCallback(callback);
	}

	/**
	 * Show Inspector.
	 */
	public showInspector(visible: boolean, elementId: string): void {
		if (visible) {
			import('@babylonjs/inspector').then(() => {
				const globalRoot = document.getElementById(elementId);
				if (globalRoot) {
					this.scene.debugLayer.show({
						globalRoot,
					});
				}
			});
		} else {
			this.scene.debugLayer.hide();
		}
	}

	/**
	 * Resize Babylon engine.
	 */
	public resize(isMobile: boolean): void {
		if (
			!this.engine ||
			!this.canvas ||
			this.canvas.clientWidth <= 0 ||
			this.canvas.clientHeight <= 0
		) {
			return;
		}

		/* Scale the canvas twice the size for mobile */
		isMobile ? this.engine.setHardwareScalingLevel(0.5) : this.engine.setHardwareScalingLevel(1);
		this.engine.resize();
	}

	/**
	 * Returns current canvas.
	 *
	 */
	public getCanvas(): HTMLCanvasElement {
		return this.canvas;
	}

	/**
	 * Returns if iOS.
	 *
	 */
	public isIOS(): boolean {
		return this.engine['_badOS'];
	}

	/**
	 * Returns scene transform matrix.
	 *
	 */
	public getTransformMatrix(): Matrix {
		return this.scene.getTransformMatrix();
	}

	/**
	 * Initializes the Babylon engine.
	 */
	protected initEngine(): Promise<Engine> {
		if (!this.canvas) {
			return Promise.reject('Canvas element was not found!');
		}
		this.canvas.addEventListener('touchstart', (event) => {
			event.preventDefault();
		});
		this.canvas.addEventListener('touchmove', (event) => {
			event.preventDefault();
		});
		this.canvas.addEventListener('touchend', (event) => {
			event.preventDefault();
		});
		this.engine = new Engine(this.canvas, true, {
			preserveDrawingBuffer: true,
			xrCompatible: false,
		});
		return Promise.resolve(this.engine);
	}

	/**
	 * Initializes the Babylon scene.
	 */
	protected initScene(engine: Engine): Promise<Scene> {
		if (this.scene) {
			this.scene.dispose();
		}
		this.scene = new Scene(engine);
		this.scene.clearColor = new Color4(0, 0, 0, 0);
		this.scene.ambientColor = Color3.White();
		this.scene.preventDefaultOnPointerDown = false;
		this.scene.preventDefaultOnPointerUp = false;
		this.handleBadDesktopOsMouseEvents();
		this.cameraManager = new CameraManager(this.scene, this.observablesManager);
		this.lightFactory = new LightFactory(this.scene, this.observablesManager);
		this.meshFactory = new MeshFactory(this.scene, this.observablesManager);
		this.pointerManager = new PointerManager(this.scene, this.observablesManager);
		this.colissionManager = new CollisionManager(this.scene, this.observablesManager);

		return Promise.resolve(this.scene);
	}

	private handleBadDesktopOsMouseEvents(): void {
		if (this.engine['_badDesktopOS'] && window.navigator.maxTouchPoints === 0) {
			this.scene.onPointerUp = () => {
				document.dispatchEvent(
					new CustomEvent('badDesktopOSMouseUp', {
						bubbles: true,
						detail: {
							position: { x: null, y: null },
							isOverDropArea: true,
						},
					})
				);
			};
		}
	}
}
